'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useState } from 'react';
import { createDesktopControlBarViewportLayout } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarDesktopLayout';
import { createEmbeddedControlBarViewportLayout } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarEmbeddedLayout';
import { createKidsModeControlBarViewportLayout } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarKidsModeLayout';
import { createMobileControlBarViewportLayout } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarMobileLayout';
import ControlBarViewportLayout, { type ControlBarViewportLayoutProps } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarViewportLayout';
import type { ControlBarPresentation } from '@/constants/control-bar';
import { EmbeddedControlBarContext } from '@/context/EmbeddedControlBarContext';
import { useUAContext } from '@/context/UAContext';
import { useWorldDetailsContext } from '@/context/WorldDetailsContext';
import type { RecommendRoom } from '@/services/cms';
type ControlBarPresentationLayoutProps = {
  children: ReactNode;
  presentation: ControlBarPresentation;
};
function createNoControlBarViewportLayout(): Omit<ControlBarViewportLayoutProps, 'children'> {
  return {
    barPlacement: 'before',
    className: 'flex h-full min-h-0 min-w-0 flex-1 flex-col',
    renderControlBar: () => null,
    showToaster: false
  };
}
function createControlBarViewportLayout(presentation: ControlBarPresentation, isFullscreenBarHidden: boolean, isMobileLandscape: boolean, moreRecommendationsPromise: Promise<RecommendRoom[]> | null, onHideAction: () => void) {
  switch (presentation.kind) {
    case 'desktop':
      return createDesktopControlBarViewportLayout({
        isHidden: isFullscreenBarHidden,
        moreRecommendationsPromise,
        onHideAction,
        surface: presentation.surface,
        variant: presentation.variant
      });
    case 'embedded':
      if (presentation.surface === 'normal') {
        return createEmbeddedControlBarViewportLayout({
          moreRecommendationsPromise,
          variant: presentation.variant
        });
      }
      if (presentation.device === 'mobile') {
        return createMobileControlBarViewportLayout({
          isMobileLandscape,
          moreRecommendationsPromise
        });
      }
      return createDesktopControlBarViewportLayout({
        isHidden: isFullscreenBarHidden,
        moreRecommendationsPromise,
        onHideAction,
        surface: 'fullscreen',
        variant: presentation.variant
      });
    case 'mobile':
      return createMobileControlBarViewportLayout({
        isMobileLandscape,
        moreRecommendationsPromise
      });
    case 'kids-mode':
      return createKidsModeControlBarViewportLayout(isMobileLandscape);
    case 'none':
      return createNoControlBarViewportLayout();
  }
}
export default function ControlBarPresentationLayout(t0) {
  const $ = _c(12);
  const {
    children,
    presentation
  } = t0;
  const {
    logoOverlayRoomsPromise
  } = useWorldDetailsContext();
  const {
    isMobileLandscape
  } = useUAContext();
  const [isFullscreenBarHidden, setIsFullscreenBarHidden] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsFullscreenBarHidden(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isFullscreenBarHidden || $[2] !== isMobileLandscape || $[3] !== logoOverlayRoomsPromise || $[4] !== presentation) {
    t2 = createControlBarViewportLayout(presentation, isFullscreenBarHidden, isMobileLandscape, logoOverlayRoomsPromise, t1);
    $[1] = isFullscreenBarHidden;
    $[2] = isMobileLandscape;
    $[3] = logoOverlayRoomsPromise;
    $[4] = presentation;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const viewportLayout = t2;
  const t3 = presentation.kind === "embedded" ? presentation.surface : null;
  let t4;
  if ($[6] !== children || $[7] !== viewportLayout) {
    t4 = <ControlBarViewportLayout {...viewportLayout}>{children}</ControlBarViewportLayout>;
    $[6] = children;
    $[7] = viewportLayout;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <EmbeddedControlBarContext value={t3}>{t4}</EmbeddedControlBarContext>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}